import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  InputBase,
  Tooltip,
  Button, // Added Button for "Analytics"
  Chip,   // Added Chip for the User Greeting
} from "@mui/material";
// Using @mui/icons-material for stability. Updated icons for Admin Portal context.
import { 
    Notifications, 
    Search, 
    HelpOutline, // Changed from CalendarToday to Help/Support
    Assessment,  // New icon for Analytics/Reports
    Logout, 
    AccountCircle,
    Settings,
} from "@mui/icons-material";
// import { useNavigate } from "react-router-dom";

// Define the consistent brand color for a professional Admin look (Indigo)
const BRAND_ACCENT = "#151058ff"; // Professional Deep Indigo

const Topbar = ({ notificationCount = 3 }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  // NOTE: useNavigate is included for completeness, but navigation is mocked via console.log
  // const navigate = useNavigate(); 

  // Mocked Admin User Data
  const userName = "Admin Jayanth";

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // NOTE: Logging instead of actual auth/storage manipulation
    console.log("Admin user logged out.");
    handleMenuClose();
    // navigate("/login", { replace: true }); // Mock navigation for demonstration
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1300,
          background: "#ffffff", // Clean white background
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)", // Subtle shadow
          borderBottom: "1px solid #e5e7eb", // Light border for definition
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", minHeight: 64 }}>
          {/* --- 1. Admin Portal Logo/Title --- */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.5,
              color: BRAND_ACCENT, // Branded Accent Color (Indigo)
              userSelect: "none",
              // Mobile friendly styling
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Technglobal.in
          </Typography>

          {/* --- 2. Search Bar --- */}
          <Box
            sx={{
              position: "relative",
              borderRadius: 8, // Fully rounded search bar
              backgroundColor: "#f1f5f9", // Light grey background
              border: "1px solid #e2e8f0",
              "&:hover": { backgroundColor: "#e2e8f0" },
              marginRight: 4,
              width: { xs: "50%", sm: "auto" },
              flexGrow: 1,
              maxWidth: 500,
            }}
          >
            <Box
              sx={{
                padding: (theme) => theme.spacing(0, 1.5),
                height: "100%",
                position: "absolute",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
              }}
            >
              <Search sx={{ fontSize: 20 }} /> 
            </Box>
            <InputBase
              placeholder="Search users, transactions, or reports..."
              inputProps={{ "aria-label": "search" }}
              sx={{
                color: "#1e293b",
                pl: "40px",
                width: "100%",
                fontSize: 14,
                "& .MuiInputBase-input": {
                  padding: "8px 12px",
                },
              }}
            />
          </Box>

          {/* --- 3. User Actions and Profile --- */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 3 } }}>
            
            {/* Analytics Button (Admin Feature) */}
            <Button 
                variant="contained" 
                size="small"
                startIcon={<Assessment />} // Icon for analytics
                sx={{ 
                    bgcolor: BRAND_ACCENT, 
                    '&:hover': { bgcolor: '#3730a3', boxShadow: '0 4px 10px rgba(67, 56, 202, 0.4)' },
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2 },
                    py: 0.8,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    boxShadow: '0 2px 4px rgba(67, 56, 202, 0.2)',
                    display: { xs: 'none', md: 'inline-flex' } // Hidden on very small screens
                }}
                onClick={() => console.log('Navigate to Analytics Dashboard')}
            >
                Analytics
            </Button>
            
            {/* Help/Support Icon (Admin Feature) */}
            <Tooltip title="Help & Support" arrow>
                <IconButton color="inherit" sx={{ color: '#475569', '&:hover': { color: BRAND_ACCENT, bgcolor: '#f1f5f9' } }}>
                    {/* Replaced CalendarToday with HelpOutline icon */}
                    <HelpOutline sx={{ fontSize: 24 }} />
                </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Alerts & Notifications" arrow>
              <IconButton
                size="large"
                aria-label={`show ${notificationCount} new notifications`}
                color="inherit"
                sx={{ color: '#475569', '&:hover': { color: BRAND_ACCENT, bgcolor: '#f1f5f9' } }}
              >
                <Badge 
                    badgeContent={notificationCount} 
                    color="error" 
                    overlap="circular"
                    sx={{
                        "& .MuiBadge-badge": {
                            fontSize: "0.8rem",
                            fontWeight: 700,
                        }
                    }}
                >
                  <Notifications sx={{ fontSize: 26 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Greeting and Avatar */}
            <Chip 
                label={`Admin, ${userName.split(' ')[1]}`}
                avatar={<Avatar alt={userName} sx={{ width: 32, height: 32, bgcolor: BRAND_ACCENT, color: '#c8e109ff' }}>{userName.charAt(0)}</Avatar>}
                onClick={handleProfileMenuOpen}
                sx={{ 
                    bgcolor: '#f1f5f9', 
                    fontWeight: 600, 
                    cursor: 'pointer', 
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#e2e8f0' },
                    display: { xs: 'none', sm: 'flex' } // Hide chip on tiny screens
                }}
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
            />

            {/* Avatar only for smaller screens */}
            <Tooltip title="Account settings" arrow>
              <IconButton
                size="large"
                edge="end"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ display: { xs: 'block', sm: 'none' } }}
              >
                <Avatar alt={userName} sx={{ width: 32, height: 32, bgcolor: BRAND_ACCENT, color: '#fbfefeff' }}>{userName.charAt(0)}</Avatar>
              </IconButton>
            </Tooltip>

          </Box>
        </Toolbar>
      </AppBar>

      {/* Account menu */}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 8, // Increased elevation for 'cool' effect
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            overflow: 'visible', // Allows for shadow/dropdown arrow effect
            filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
            "& .MuiMenuItem-root": {
              typography: "body2",
              px: 3,
              py: 1.2,
              fontWeight: 500,
              color: '#334155',
              transition: 'background-color 0.2s',
              "&:hover": {
                backgroundColor: `${BRAND_ACCENT}10`, // Subtle hover effect
                color: BRAND_ACCENT,
              }
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleMenuClose}>
            <AccountCircle sx={{ mr: 1, fontSize: 20 }} />
            Manage Profile
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
            <Settings sx={{ mr: 1, fontSize: 20 }} />
            System Settings
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: '#dc2626 !important' }}>
            <Logout sx={{ mr: 1, fontSize: 20 }} />
            Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default Topbar;  